import { defineStore } from 'pinia'
import { getMailPreferences, updateMailPreferences } from '@/api/v2/mail_preferences'
import type { HiddenFolder } from '@/models/MailFolder'
import { extractApiErrors } from '@/utils/apiErrors'

interface State {
  /** What the sidebar shows: the confirmed state plus pending optimistic changes. */
  hidden: HiddenFolder[]
  /** The last state the server confirmed; a failed save falls back to it. */
  confirmed: HiddenFolder[]
  loaded: boolean
  /** Saves in flight. */
  pending: number
  /** Bumped after every confirmed change, so the list reloads with the server-side filter. */
  version: number
  /** Bumped by a failed save; saves queued before it are dropped. */
  generation: number
  /** Saves started so far; a preferences GET older than a save is ignored. */
  saves: number
  /** Error code of the last failed save (`FOLDER_NOT_VISIBLE`), null for other failures. */
  lastErrorCode: string | null
}

const key = (f: HiddenFolder): string => f.connection_id + ':' + f.folder_id

function same(a: HiddenFolder, b: HiddenFolder): boolean {
  return a.connection_id === b.connection_id && a.folder_id === b.folder_id
}

/**
 * Per-user settings of the mail viewer (hidden folders), stored on the server. Changes are shown
 * at once and saved one after the other; a failed save rolls back to the server's state.
 */
export const useMailViewStore = defineStore('mailView', {
  state: (): State => ({ hidden: [], confirmed: [], loaded: false, pending: 0, version: 0, generation: 0, lastErrorCode: null, saves: 0 }),

  getters: {
    hiddenKeys: (state): Set<string> => new Set(state.hidden.map(key)),
  },

  actions: {
    async load(): Promise<void> {
      const savesBefore = this.saves
      const session = epoch
      const resp = await getMailPreferences()
      // The session ended meanwhile (logout, account switch): the answer is the previous user's.
      if (session !== epoch) return
      // A save started meanwhile already brought (or will bring) a newer server state; this
      // answer may predate it and would silently undo it.
      if (this.saves !== savesBefore) {
        this.loaded = true
        return
      }
      const list = resp.data.data?.hidden_folders ?? []
      this.confirmed = list
      // A save started while loading wins; its answer brings the server state anyway.
      if (this.pending === 0) this.hidden = [...list]
      this.loaded = true
    },

    isHidden(folder: HiddenFolder): boolean {
      return this.hidden.some((f) => same(f, folder))
    },

    /** Hides or shows one folder (its subfolders follow on the server). */
    toggle(folder: HiddenFolder): Promise<boolean> {
      const next = this.isHidden(folder) ? this.hidden.filter((f) => !same(f, folder)) : [...this.hidden, folder]
      return this.save(next)
    },

    showAll(): Promise<boolean> {
      return this.save([])
    },

    /**
     * Shows `next` at once and saves it. Saves run in order (the chain), so the server ends up
     * with the last state the user produced. Resolves false when the save failed or was dropped
     * because an earlier one failed; the sidebar then shows the server's state again.
     */
    save(next: HiddenFolder[]): Promise<boolean> {
      this.hidden = next
      this.pending++
      this.saves++
      const generation = this.generation
      const session = epoch
      const run = chain.then(async () => {
        // Queued by the previous session: neither send it nor touch the reset state.
        if (session !== epoch) return false
        try {
          // A save queued on top of one that failed would bring the rolled back change back.
          if (generation !== this.generation) return false
          try {
            const resp = await updateMailPreferences({ hidden_folders: next })
            if (session !== epoch) return false
            this.confirmed = resp.data.data?.hidden_folders ?? next
            this.version++
            return true
          } catch (err) {
            if (session !== epoch) return false
            // 422 FOLDER_NOT_VISIBLE: a mailbox in the list is no longer visible to the caller.
            const { code, messages } = extractApiErrors(err)
            this.lastErrorCode = [code, ...messages].some((m) => m?.startsWith('FOLDER_NOT_VISIBLE')) ? 'FOLDER_NOT_VISIBLE' : null
            this.generation++
            this.hidden = [...this.confirmed]
            return false
          }
        } finally {
          if (session === epoch) {
            this.pending--
            // The server may prune entries (deleted folders); adopt its answer once nothing is queued.
            if (this.pending === 0) this.hidden = [...this.confirmed]
          }
        }
      })
      chain = run
      return run
    },
  },
})

let chain: Promise<unknown> = Promise.resolve()
/** Bumped when a session ends; answers and queued saves of an older session are dropped. */
let epoch = 0

/**
 * A fresh save queue. Called on logout and account switch (see stores/mailSession.ts) and per
 * test: saves still queued and preferences answers still in flight belong to the previous
 * session and must neither reach the server for the next user nor land in the reset store.
 */
export function resetMailViewQueue(): void {
  chain = Promise.resolve()
  epoch++
}
